import { Card, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { PointsOverviewChart } from '@/pages/RetailerOverview/PointsOverview/PointsOverviewChart';
import {
  PointsOverviewToolbar,
} from '@/pages/RetailerOverview/PointsOverview/PointsOverviewToolbar';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RetailerEndpoint } from '@api/endpoints';
import { ApiRetailer } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';


interface PointsOverviewCardProps {
  fullWidth?: boolean,
  retailer: ApiRetailer,
  selectedProgram: ApiRewardsProgram | undefined,
}

export const PointsOverviewCard = ({
  fullWidth = false,
  retailer,
  selectedProgram,
}: PointsOverviewCardProps) => {
  const { t } = useTranslation();
  const [queryBody, setQueryBody] = useState<RetailerEndpoint.GetPointsOverview.Request>(
    { programId: selectedProgram?.id },
  );
  const isAnyFilterSelected = !!Object.keys(queryBody).length;
  const hasMultipleRewardPrograms = agronomyProgramsOnly(retailer.rewardsPrograms).length > 1;

  useEffect(() => {
    setQueryBody((prevState) => ({ ...prevState, programId: selectedProgram?.id }));
  }, [selectedProgram]);

  const { data: pointsOverviewPeriods } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW, retailer.id, queryBody],
    () => RetailerApi.getPointsOverview(
      retailer.id,
      isAnyFilterSelected ? queryBody : undefined,
    ),
  );

  const filterIds = retailer.isProgramCreationDisabled ? [
    'date-range', 'wallet', 'category', 'salesperson', 'customer', 'location',
  ] : [
    'date-range', 'origin', 'wallet', 'category', 'salesperson', 'customer', 'location',
  ];

  return (
    <Card
      cardContentStyle={{ height: '100%', padding: 0, width: '100%' }}
      sx={{ height: '556px', padding: '20px 20px 24px', width: fullWidth ? '100%' : '804px' }}
      testID="points-overview-card"
    >
      <Text fontSize="18px" fontWeight={500}>
        {hasMultipleRewardPrograms 
          ? t($ => $.overview.pointsEngagement.titleWithProgram)
          : t($ => $.overview.pointsEngagement.title)
        }
      </Text>
      <VSpacer size="4" />
      <PointsOverviewToolbar
        filterIds={filterIds}
        onChange={(body) => setQueryBody({
          ...body,
          programId: selectedProgram?.id,
        })}
        retailer={retailer}
        selectedProgramId={selectedProgram?.id}
      />
      {!retailer.isProgramCreationDisabled && (
        <>
          <Text category="body-small" textAlign="right">
            {t($ => $.overview.selectedProgramPeriodOnly)}
          </Text>
          <VSpacer size="5"/>
        </>
      )}
      <PointsOverviewChart periods={pointsOverviewPeriods ?? []} retailer={retailer}/>
    </Card>
  );
};
